import {DateTime} from 'luxon'

import {GetBookingCancelDetailsReturnType} from '../../../api/booking/get-booking-cancel-details-data'
import {RainCheckOffer} from '../../../api/booking/get-booking-rain-check'

// What the Rain check card says for each state the server can answer. Pure: it returns translation
// keys and values, never strings, so it is the one place to read "state -> content" and test it.
// Every number printed here is copied from the server answer; nothing is computed or sent back.

export type RainCheckLine = {
  key: string
  params?: Record<string, number | string>
}

export type RainCheckAction = {
  kind: 'cancel' | 'contact'
  labelKey: string
}

export type RainCheckCardContent = {
  tone: 'done' | 'info' | 'offer'
  titleKey: 'rain_check_title' | 'rain_check_used_title'
  reason: string | null
  endsLine: RainCheckLine | null
  policyText: string | null
  bookingName: string | null
  bookingWhen: string | null
  moneyLine: RainCheckLine | null
  shareReason: string | null
  body: RainCheckLine | null
  action: RainCheckAction | null
}

export type RainCheckContentContext = {
  // The page's own money formatter (venue currency helper), so the card prints money like the page.
  formatMoney: (value: number, currency?: string | null) => string
  // The venue-clock time of the offer's end, already formatted for the venue timezone.
  formatTime: (value?: number | string | null) => string | null
  // The booking's time as the booking page prints it (the card never converts the booking's own clock),
  // and its name for when the answer lacks one.
  fallbackBookingName?: string | null
  fallbackBookingWhen?: string | null
  // False when the page has no way to reach the venue; the external state then has no button.
  canContactVenue?: boolean
}

const text = (value?: string | null): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed || null
}

const isAmount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

const empty: RainCheckCardContent = {
  tone: 'offer',
  titleKey: 'rain_check_title',
  reason: null,
  endsLine: null,
  policyText: null,
  bookingName: null,
  bookingWhen: null,
  moneyLine: null,
  shareReason: null,
  body: null,
  action: null,
}

const claimedBody = (offer: RainCheckOffer, ctx: RainCheckContentContext): RainCheckLine => {
  const claim = offer.claim
  if (claim?.status === 'refunded') {
    return isAmount(claim.refunded) && claim.refunded > 0
      ? {
          key: 'rain_check_claimed_refunded',
          params: {refunded: ctx.formatMoney(claim.refunded, claim.currency ?? offer.currency)},
        }
      : {key: 'rain_check_claimed_refunded_no_amount'}
  }
  if (claim?.status === 'cancelled') {
    return {key: 'rain_check_claimed_cancelled'}
  }
  if (claim?.status === 'needs_a_look') {
    return {key: 'rain_check_claimed_needs_a_look'}
  }
  return {key: 'rain_check_claimed_generic'}
}

// Returns null when there is nothing to show (state none, or an answer we cannot say honestly).
export const getRainCheckCardContent = (
  offer: RainCheckOffer | null | undefined,
  ctx: RainCheckContentContext,
): RainCheckCardContent | null => {
  if (!offer) {
    return null
  }

  if (offer.state === 'claimed') {
    return {
      ...empty,
      tone: 'done',
      titleKey: 'rain_check_used_title',
      body: claimedBody(offer, ctx),
    }
  }

  if (offer.state !== 'open' && offer.state !== 'free' && offer.state !== 'external') {
    return null
  }

  const ends = ctx.formatTime(offer.endsAt)
  const base: RainCheckCardContent = {
    ...empty,
    tone: offer.state === 'external' ? 'info' : 'offer',
    reason: text(offer.reason),
    endsLine: ends ? {key: 'rain_check_offer_ends', params: {time: ends}} : null,
    policyText: text(offer.policy?.customerText),
    bookingName: text(offer.booking?.name) ?? text(ctx.fallbackBookingName),
    bookingWhen: text(ctx.fallbackBookingWhen),
  }

  if (offer.state === 'open') {
    const refund = offer.refundAmount
    if (!isAmount(refund)) {
      return null
    }
    const percent = offer.policy?.refundPercent
    return {
      ...base,
      moneyLine: isAmount(offer.paid)
        ? {
            key: 'rain_check_money_line',
            params: {
              paid: ctx.formatMoney(offer.paid, offer.currency),
              refund: ctx.formatMoney(refund, offer.currency),
            },
          }
        : {key: 'rain_check_money_refund_only', params: {refund: ctx.formatMoney(refund, offer.currency)}},
      // The share is only explained when it is less than everything: the offer stays honest.
      shareReason: isAmount(percent) && percent < 100 ? text(offer.policy?.shareReason) : null,
      action: {kind: 'cancel', labelKey: 'rain_check_cancel_get_money'},
    }
  }

  if (offer.state === 'free') {
    // "Nothing was paid online" is only said when the server says nothing was paid.
    const paidSomething = isAmount(offer.paid) && offer.paid > 0
    // A pass session goes back to the pass: no money line and never "nothing was paid".
    const bodyKey =
      offer.returnsPassSession === true
        ? 'rain_check_free_text_pass'
        : paidSomething
        ? 'rain_check_free_text_paid'
        : 'rain_check_free_text'
    return {
      ...base,
      body: {key: bodyKey},
      action: {kind: 'cancel', labelKey: 'rain_check_cancel_free'},
    }
  }

  return {
    ...base,
    // A missing or unknown reason is the old behaviour (paid at the venue).
    body: {
      key: offer.externalReason === 'several_payments' ? 'rain_check_external_text_parts' : 'rain_check_external_text',
    },
    action: ctx.canContactVenue === false ? null : {kind: 'contact', labelKey: 'rain_check_contact_venue'},
  }
}

const SYMBOLIC_CURRENCIES = ['THB', 'EUR', 'USD', 'UAH', 'GBP', 'ILS']

// Whole-currency amounts as the server sends them, rounded for the eye only (no 333.3299999).
export const roundForDisplay = (value: number): number => Math.round(value * 100) / 100

// Money in the repo's own way (`l('THB_symbolic', {value})`); a currency without a symbolic string is
// printed as "<amount> <CODE>" instead of showing a missing-translation marker.
export const formatRainCheckMoney = (
  l: (key: string, options?: Record<string, number | string>) => string,
  value: number,
  currency?: string | null,
): string => {
  const code = (currency || '').toUpperCase()
  const rounded = roundForDisplay(value)
  if (SYMBOLIC_CURRENCIES.includes(code)) {
    return l(`${code}_symbolic`, {value: rounded})
  }
  return code ? `${rounded} ${code}` : String(rounded)
}

// The offer's end is real UTC; show it on the venue clock. A bad or missing zone falls back to the
// browser's. Today's end shows the time only; another day adds the date.
export const formatRainCheckTime = (
  value: number | string | null | undefined,
  zone?: string | null,
  now: DateTime = DateTime.now(),
): string | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed =
    typeof value === 'number' || /^\d+$/.test(String(value))
      ? DateTime.fromMillis(Number(value))
      : DateTime.fromISO(String(value))
  if (!parsed.isValid) {
    return null
  }
  const zoned = zone && parsed.setZone(zone).isValid ? parsed.setZone(zone) : parsed
  const nowZoned = zone && now.setZone(zone).isValid ? now.setZone(zone) : now
  return zoned.hasSame(nowZoned, 'day') ? zoned.toFormat('HH:mm') : zoned.toFormat('MMM dd, HH:mm')
}

export type RainCheckCancelView = {
  reason: string | null
  policyText: string | null
  // What the server will refund (its venueCancelQuote), or 0 when nothing goes back.
  refundable: number
  // What the share keeps back, only when the server says some is kept.
  notReturned: number | null
  shareReason: string | null
  // Something was paid but nothing goes back (the card says "free", not "nothing was paid").
  paidSomething: boolean
}

// The cancel modal under a rain check. Null (the ordinary modal, exactly as before) unless the server
// sent `rainCheck` AND an amount to print: a server that did not price it must not be quoted for it.
// The amount is the server's rain-check quote; the client never works one out.
export const getRainCheckCancelView = (details?: GetBookingCancelDetailsReturnType): RainCheckCancelView | null => {
  const rain = details?.rainCheck
  if (!details || !rain) {
    return null
  }
  const quote = details.venueCancelQuote
  const refundable =
    quote?.rainCheck === true && isAmount(quote.refundable)
      ? quote.refundable
      : isAmount(details.availableAmountForRefund)
      ? details.availableAmountForRefund
      : null
  if (refundable === null) {
    return null
  }
  const percent = rain.refundPercent
  const withheld = quote?.rainCheck === true && isAmount(quote.withheld) && quote.withheld > 0 ? quote.withheld : null
  return {
    reason: text(rain.reason),
    policyText: text(rain.customerText),
    refundable,
    notReturned: withheld,
    shareReason: withheld !== null && (!isAmount(percent) || percent < 100) ? text(rain.shareReason) : null,
    paidSomething: isAmount(details.totalPaidAmount) && details.totalPaidAmount > 0,
  }
}
